import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import ascii from "../data/heroAscii.json";

const assetRoot = `${process.env.PUBLIC_URL || ""}/videos/assistant`;
const revealPlaybackRate = 0.9;
const revealColumns = Array.from({ length: ascii.gridColumns }, (_, index) => {
  const random = (seed) => {
    const value = Math.sin(seed * 127.1 + 19.3) * 43758.5453;
    return value - Math.floor(value);
  };
  return {
    delay: random(index + 1) * 0.85 / revealPlaybackRate,
    duration: (1.15 + random(index + 101)) / revealPlaybackRate,
  };
});
// Leave a small hidden interval for the native loop's timeupdate event.
const departureLeadTime = Math.max(...revealColumns.map(({ delay, duration }) => delay + duration)) + 0.5;

const AssistantVideo = ({ theme }) => {
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const previousVideoTime = useRef(0);
  const id = `assistant-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [failed, setFailed] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const [revealCycle, setRevealCycle] = useState(0);
  const [revealPhase, setRevealPhase] = useState("arrive");
  const [reducedMotion, setReducedMotion] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const markReady = useCallback(() => {
    stageRef.current?.setAttribute("data-reveal-ready", "true");
  }, []);
  const handleTimeUpdate = useCallback((event) => {
    const { currentTime, duration } = event.currentTarget;
    // Native looping seeks back to the start without firing an ended event.
    if (currentTime < previousVideoTime.current) {
      setRevealCycle(cycle => cycle + 1);
    }
    setRevealPhase(Number.isFinite(duration) && currentTime >= duration - departureLeadTime ? "depart" : "arrive");
    previousVideoTime.current = currentTime;
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const stage = stageRef.current;
    if (!stage) return undefined;
    let disposed = false;
    const rect = (video || stage).getBoundingClientRect();
    let inView = rect.bottom > 0 && rect.top < window.innerHeight;

    const syncPlayback = () => {
      const active = !disposed && inView && !document.hidden && !reducedMotion;
      stage.style.setProperty("--assistant-reveal-state", active ? "running" : "paused");
      if (video && !failed && active) {
        video.muted = true;
        // Keep the matching poster if browser policy denies muted autoplay.
        video.play()?.catch(markReady);
      } else {
        video?.pause();
      }
    };
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      syncPlayback();
    }, { threshold: 0.05 });
    observer?.observe(stage);
    document.addEventListener("visibilitychange", syncPlayback);
    syncPlayback();
    return () => {
      disposed = true;
      observer?.disconnect();
      document.removeEventListener("visibilitychange", syncPlayback);
      video?.pause();
    };
  }, [reducedMotion, failed, usingFallback, markReady]);

  const handleMediaError = () => {
    if (!usingFallback) setUsingFallback(true);
    else setFailed(true);
  };

  return (
    <div
      ref={stageRef}
      className={`assistant-video-stage ${theme === "light" ? "assistant-video-light" : "assistant-video-dark"}${usingFallback && !failed ? " assistant-video-fallback" : ""}`}
      data-reveal-phase={revealPhase}
      style={{ "--assistant-reveal-mask": `url(#${id}-mask)` }}
    >
      <svg width="0" height="0" className="assistant-video-mask-defs" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={`${id}-fall`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="80%" stopColor="white" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <mask id={`${id}-mask`} maskUnits="objectBoundingBox" maskContentUnits="objectBoundingBox" x="0" y="0" width="1" height="1" style={{ maskType: "alpha" }}>
            {revealColumns.map(({ delay, duration }, index) => (
              <rect
                key={`${revealCycle}-${index}`}
                className="assistant-video-reveal-column"
                x={index / ascii.gridColumns - 0.0015}
                y="0"
                width={1 / ascii.gridColumns + 0.003}
                height="1.4"
                fill={`url(#${id}-fall)`}
                style={{ "--column-delay": `${delay}s`, "--column-duration": `${duration}s` }}
              />
            ))}
          </mask>
        </defs>
      </svg>
      <div className="assistant-video-figure" aria-hidden="true">
        <div className="assistant-video-frame">
          <div className="assistant-video-reveal">
            {failed ? (
              <img className="assistant-video-media" src={`${assetRoot}/${ascii.posterFile}`} alt="" onLoad={markReady} />
            ) : (
              <video
                ref={videoRef}
                className="assistant-video-media"
                muted
                loop
                playsInline
                preload="none"
                src={`${assetRoot}/${usingFallback ? ascii.videoFile : ascii.alphaVideoFile}`}
                poster={`${assetRoot}/${ascii.posterFile}`}
                onLoadedData={markReady}
                onTimeUpdate={handleTimeUpdate}
                onError={handleMediaError}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistantVideo;
